import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { useActiveConversation } from '../../chat/ActiveConversationContext';
import { PACK_HANDOFF_PARAM } from '../../../lib/pack-handoff';
import { initialChatMode, readChatModeFromUrl } from './chat-mode';

// A CTA that navigates to `/packages` while ALSO calling `leaveChat()` (which changes `chatMode`) must
// call this FIRST, synchronously, in the same click — see the comment on `sync` below for why. Module
// state rather than a ref: the CTA's `onClick` lives in an unrelated hook/component tree with no access
// to this one's refs, and the two only need to agree on ONE bit for the length of a single click.
//
// Consumed (cleared) by whichever `sync()` call happens to read it next — deliberately NOT on a timer
// or a microtask. Measured: a click's own state flush does not reliably land before a microtask queued
// during that same click, so a "self-clearing" timer cleared this before `sync` ever ran, which is what
// made the original version of this fix flaky. Leaving it consumption-only means the worst case is the
// opposite failure — a `chatMode` change that turns out NOT to trigger `sync` (because `leaveChat()` was
// a no-op) leaves the flag sitting `true` until the NEXT `sync` call, which the `routeChangeComplete`
// listener guarantees happens on the very navigation this click already started.
let suppressNextSync = false;
export function skipChatUrlSyncOnce() {
  suppressNextSync = true;
}

// Keeps the chat's size (`?chat`) and open thread (`?conversation`) in sync with
// the URL in both directions: it adopts a deep-link's size on first mount and
// opens on a handoff/greeting/pending-intent, and it is the single writer of both
// query params so a refresh (or a shared link) restores exactly what's on screen.
export function useChatUrlSync() {
  const router = useRouter();
  const { activeConversationId, chatMode, setChatMode, pendingChatAction, pendingUploadFiles, briefPeekId, conversationInUrl } = useActiveConversation();

  const initedRef = useRef(false);
  const prevHandoffRef = useRef(false);
  useEffect(() => {
    // A pack accept forwards here in-app (`PackAcceptScreen`), with this hook already mounted, so its
    // marker opens the chat the same way a new-chat handoff does. The accept landing itself is never a
    // handoff, even when its invite carries a `prefill`: that prompt is for the chat it forwards into
    // (`ChatSurface` ignores it there too). Read as one here, it opened the chat over the accept loader,
    // and the forward that followed was no longer a new handoff, so the chat stayed out of fullscreen.
    const onPackAccept = router.pathname === '/pack-accept';
    const handoff = !onPackAccept && (router.query.new === '1' || typeof router.query.prefill === 'string' || router.query[PACK_HANDOFF_PARAM] === '1');
    if (!initedRef.current) {
      initedRef.current = true;
      prevHandoffRef.current = handoff;
      const opening = initialChatMode({
        restored: readChatModeFromUrl(),
        handoff,
        hasConversation: typeof router.query.conversation === 'string',
      });
      if (opening) setChatMode(opening);
      return;
    }
    if (handoff && !prevHandoffRef.current) setChatMode(router.query.chat === 'fullscreen' ? 'fullscreen' : 'expanded');
    prevHandoffRef.current = handoff;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.asPath]);

  // A stashed intent (start a call / attach files) is itself an open intent — it's
  // consumed by ChatSurface, which a bubble doesn't mount, and the URL transition
  // above misses the repeat case (the URL is often already ?new=1).
  useEffect(() => {
    if ((pendingChatAction || pendingUploadFiles) && chatMode === 'bubble') {
      setChatMode('expanded');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingChatAction, pendingUploadFiles]);

  const activeConversationRef = useRef(activeConversationId);
  activeConversationRef.current = activeConversationId;
  const chatModeRef = useRef(chatMode);
  chatModeRef.current = chatMode;
  // The morning-brief peek is transient — never stamp it into the URL, so a refresh
  // returns to the last real chat rather than resuming the brief.
  const briefPeekRef = useRef(briefPeekId);
  briefPeekRef.current = briefPeekId;
  // Only a deliberately-opened thread belongs in the URL. An ambiently-resumed
  // thread (from sessionStorage / the recent list) stays out of the address bar —
  // context + sessionStorage still resume it across refresh and routes.
  const conversationInUrlRef = useRef(conversationInUrl);
  conversationInUrlRef.current = conversationInUrl;
  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Written via the History API (not the Next router) so it stays off
    // router.query — never fighting the autosend/handoff reads — and re-asserted
    // after each navigation, which drops query params.
    const sync = () => {
      // A CTA that navigates to `/packages` AND calls `leaveChat()` changes `chatMode`, which re-runs
      // THIS effect on the SAME click, before the pending `router.push('/packages')` has committed its
      // own history entry — measured: `routeChangeStart` for that push fires AFTER this effect's
      // dependency-triggered call, not before, so gating on the router's events is already too late.
      // `skipChatUrlSyncOnce()` is the fix: it runs synchronously inside the click, ahead of the state
      // change that re-triggers this effect, so the flag is already set by the time we get here. Without
      // it, this still reads the OLD pathname, fails the `endsWith('/packages')` guard below, and stamps
      // chat params onto `/` via a raw `history.replaceState` — which moves the URL out from under
      // Next's in-flight transition, so Next treats it as superseded and drops the push, leaving the
      // reader on `/?chat=panel` instead of `/packages`.
      if (suppressNextSync) {
        suppressNextSync = false;
        return;
      }
      const url = new URL(window.location.href);
      // The picker is reached from CTAs that mean to leave chat behind, not carry it along — e.g. the
      // personal-sale banner's CTA, tapped from inside a fullscreen conversation, must land on a plain
      // `/packages?origin=…` rather than have this re-stamp `?conversation=…&chat=fullscreen` onto it the
      // instant the navigation completes (chat itself is untouched — mounted app-wide, it keeps running
      // in the background; only the URL is left alone). `endsWith` rather than an exact match so this
      // still catches the route behind every mount prefix (`/orion-web/packages`, `/symphony-web/packages`).
      if (url.pathname.endsWith('/packages')) return;
      const p = url.searchParams;
      if (url.pathname.endsWith('/chats') && chatModeRef.current === 'bubble') {
        p.delete('conversation');
        p.delete('message');
        p.delete('chat');
        p.delete('new');
        if (url.href !== window.location.href) window.history.replaceState(window.history.state, '', url.href);
        return;
      }
      const conv = briefPeekRef.current || !conversationInUrlRef.current ? null : activeConversationRef.current;
      const mode = chatModeRef.current;
      if (conv) p.set('conversation', conv);
      else p.delete('conversation');
      if (mode === 'bubble' && !conv) p.delete('chat');
      else p.set('chat', mode);
      if (url.href !== window.location.href) {
        window.history.replaceState(window.history.state, '', url.href);
      }
    };
    sync();
    router.events.on('routeChangeComplete', sync);
    return () => router.events.off('routeChangeComplete', sync);
  }, [activeConversationId, chatMode, briefPeekId, conversationInUrl, router.events, router.pathname]);
}
